import { CreateCustomOptionModal } from '@g17eco/molecules/select/partials/CreateCustomOptionModal';
import { CustomOptionMenuList } from '@g17eco/molecules/select/partials/CustomOptionMenuList';
import { SelectFactory, SelectTypes, Option } from '@g17eco/molecules/select/SelectFactory';
import { useState } from 'react';
import { ActionMeta, SingleValue } from 'react-select';
import { initCustomOptions } from './selectUtils';

interface SelectComponentParams {
  options: Option<string>[];
  setOptions: React.Dispatch<React.SetStateAction<Option<string>[]>>;
  value: Option<string>[];
  placeholder: string | undefined;
  allowCustomOptions: boolean;
  isDisabled?: boolean;
  handleChange: (newValues?: string[] | string) => void;
  isMulti: boolean;
  limit?: number;
  isReadOnlyOptions?: boolean;
  onSaveCustomOption?: (code: string) => Promise<void>;
}

export const SelectComponent = (props: SelectComponentParams) => {
  const {
    placeholder,
    allowCustomOptions = false,
    isDisabled = false,
    handleChange,
    value,
    options,
    setOptions,
    isMulti = false,
    limit,
    isReadOnlyOptions,
    onSaveCustomOption,
  } = props;

  const [showCreateModal, setShowCreateModal] = useState(false);

  const onChangeHandler = (newValue?: string | string[], actionMeta?: ActionMeta<Option<string> | null>) => {
    if (Array.isArray(newValue)) {
      if (actionMeta?.action === 'create-option') {
        // Find new options that were added
        const optionValues = options.map((v) => v.value);
        const newOptions = newValue.filter((v) => !optionValues.includes(v));

        newOptions.forEach((option) => onSaveCustomOption?.(option.trim()));
        setOptions(initCustomOptions(newValue, options, allowCustomOptions));
      }
      handleChange(newValue);
      return;
    }

    if (newValue && actionMeta?.action === 'create-option') {
      // For single select, newValue is the new option
      onSaveCustomOption?.(newValue.trim());

      setOptions(initCustomOptions([newValue], options, allowCustomOptions));
    }
    handleChange(newValue);
  };

  const baseSelectProps = {
    placeholder: <span className='text-ThemeTextPlaceholder'>{placeholder}</span>,
    isDisabled: isDisabled && !isReadOnlyOptions,
    options,
    className: 'w-100 valueList-react-select',
    isClearable: true,
  };

  if (allowCustomOptions) {
    const onAddOption = (option: string) => {
      const newValue = isMulti ? [...value.map((v) => v.value), option] : option;
      onChangeHandler(newValue, { action: 'create-option', option: { label: option, value: option } });
    };

    return (
      <>
        <CreateCustomOptionModal
          key={`CreateCustomOptionModal-${showCreateModal ? 'open' : 'closed'}`}
          isOpen={showCreateModal}
          addOption={onAddOption}
          toggle={() => setShowCreateModal(false)}
        />
        {isMulti ? (
          <SelectFactory
            key={`CreatableMultipleSelect-${showCreateModal ? 'open' : 'closed'}`} // To force re-render so it can be selected again after CreateCustomOptionModal is closed
            selectType={SelectTypes.CreatableMultipleSelect}
            {...baseSelectProps}
            onChange={(values: string[], actionMeta?: ActionMeta<Option<string> | null>) =>
              onChangeHandler(values, actionMeta)
            }
            values={value.map((op) => op.value)}
            components={{ MenuList: (props) => CustomOptionMenuList({ ...props, setShowCreateModal }) }}
            showSelectedOptions
            isFlexibleSize
          />
        ) : (
          <SelectFactory
            key={`CreatableSelect-${showCreateModal ? 'open' : 'closed'}`} // To force re-render so it can be selected again after CreateCustomOptionModal is closed
            selectType={SelectTypes.CreatableSelect}
            {...baseSelectProps}
            onChange={(op: SingleValue<Option<string> | null>, actionMeta?: ActionMeta<Option<string> | null>) =>
              onChangeHandler(op?.value, actionMeta)
            }
            value={value[0] ?? null}
            components={{ MenuList: (props) => CustomOptionMenuList({ ...props, setShowCreateModal }) }}
          />
        )}
      </>
    );
  }

  return isMulti ? (
    <SelectFactory
      selectType={SelectTypes.MultipleSelect}
      {...baseSelectProps}
      onChange={(values: string[], actionMeta?: ActionMeta<Option<string> | null>) =>
        onChangeHandler(values, actionMeta)
      }
      values={value.map((op) => op.value)}
      limit={limit}
      indicateHasReachedLimit
      // only show "select all" option if limit is not defined
      showSelectAll={limit === undefined}
      showSelectedOptions
      isFlexibleSize
      hideCheckboxOnDisabled={false}
      isReadOnlyOptions={isReadOnlyOptions}
    />
  ) : (
    <SelectFactory
      selectType={SelectTypes.SingleSelect}
      {...baseSelectProps}
      onChange={(op: SingleValue<Option<string> | null>) => onChangeHandler(op?.value)}
      value={value[0] ?? null}
    />
  );
};
